import { useEffect, useRef } from 'react';
import { IImage, Mode } from '.';

type State = {
  index: number;
  status: 'pending' | 'locked' | 'inProgress';
};

const scrollContainer = (el: HTMLElement, imgCount: number, dir: 1 | -1) => {
  const stepSize = (el.scrollHeight - el.scrollHeight / (imgCount + 1)) / imgCount;

  el.scrollTo({
    top: el.scrollTop + dir * stepSize,
    behavior: 'smooth',
  });
};

export const useScrollControl = (mode: Mode, desktopImages: IImage[]) => {
  const container = useRef<HTMLDivElement>(null);
  const desktopContainer = useRef<HTMLDivElement>(null);
  const touchStartY = useRef(0);
  const mobileContainer = useRef<HTMLDivElement>(null);
  const offsetEl = useRef<HTMLDivElement | null>(null);
  const messageRef = useRef<HTMLDivElement>(null);

  const state = useRef<State>({
    index: 0,
    status: 'pending',
  });

  const onScroll = () => {
    if (!container.current || !mobileContainer.current || !desktopContainer.current) {
      return;
    }

    const { top } =
      messageRef.current?.getBoundingClientRect() || container.current.getBoundingClientRect();

    offsetEl.current = offsetEl.current || document.querySelector('[data-main]');
    const offset = offsetEl.current?.clientHeight || 96;
    const pastBy = top - offset;
    const imageCount = desktopImages.length;
    const maxIndex = desktopImages.length - 1;
    const atStart = state.current.index === 0;
    const atBottom = state.current.index === maxIndex;

    if (state.current.status === 'locked') {
      window.scrollTo(0, window.scrollY + pastBy);
      return;
    }

    if ((pastBy >= 0 && atStart) || (pastBy < 0 && atBottom)) {
      state.current.status = 'pending';
      return;
    }

    if (state.current.status === 'pending') {
      state.current.status = 'locked';
      window.scrollTo(0, window.scrollY + pastBy);

      setTimeout(() => {
        state.current.status = 'inProgress';
      }, 800);

      return;
    }

    window.scrollTo(0, window.scrollY + pastBy);
    state.current.status = 'locked';
    setTimeout(() => {
      state.current.status = 'inProgress';
    }, 800);

    if (pastBy < 0) {
      state.current.index = Math.min(maxIndex, state.current.index + 1);
      scrollContainer(desktopContainer.current, imageCount, 1);
      scrollContainer(mobileContainer.current, imageCount, 1);
    }

    if (pastBy > 0) {
      state.current.index = Math.max(0, state.current.index - 1);
      scrollContainer(desktopContainer.current, imageCount, -1);
      scrollContainer(mobileContainer.current, imageCount, -1);
    }
  };

  const preventDefault = (e: Event) => {
    if (e.cancelable) {
      e.preventDefault();
    }
  };

  const handleScroll = (e: Event, direction: 1 | -1) => {
    if (!desktopContainer.current || !mobileContainer.current) {
      return;
    }

    if (state.current.status === 'locked') {
      preventDefault(e);
      return;
    }

    if (state.current.status === 'inProgress') {
      const imageCount = desktopImages.length;
      const maxIndex = desktopImages.length - 1;

      // Scroll down and already at last image
      if (direction > 0 && state.current.index === imageCount - 1) {
        return;
      }

      // Scroll up and already at first image
      if (direction < 0 && state.current.index === 0) {
        return;
      }

      state.current.index = Math.max(0, Math.min(maxIndex, state.current.index + direction));

      const isEdge =
        direction > 0 ? state.current.index === imageCount - 1 : state.current.index === 0;

      scrollContainer(desktopContainer.current, imageCount, direction);
      scrollContainer(mobileContainer.current, imageCount, direction);
      preventDefault(e);
      state.current.status = 'locked';

      if (isEdge) {
        setTimeout(() => {
          state.current.status = 'pending';
        }, 800);
      } else {
        setTimeout(() => {
          state.current.status = 'inProgress';
        }, 800);
      }
      return;
    }
  };

  const onWheel = (e: WheelEvent) => {
    handleScroll(e, e.deltaY > 0 ? 1 : -1);
  };

  const onTouchMove = (e: TouchEvent) => {
    handleScroll(e, e.touches[0].clientY < touchStartY.current ? 1 : -1);
  };

  const handleLock = (e: Event) => {
    if (e.type === 'touchstart') {
      touchStartY.current = (e as TouchEvent).touches[0].clientY;
    }

    if (state.current.status === 'locked') {
      e.preventDefault();
    }
  };

  useEffect(() => {
    if (mode === 'scroll') {
      window.addEventListener('scroll', onScroll, { passive: false });
      window.addEventListener('wheel', onWheel, { passive: false });
      window.addEventListener('touchstart', handleLock, { passive: false });
      window.addEventListener('touchmove', onTouchMove, { passive: false });

      return () => {
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('wheel', onWheel);
        window.removeEventListener('touchstart', handleLock);
        window.removeEventListener('touchmove', onTouchMove);
      };
    }
  }, []);

  return {
    container,
    desktopContainer,
    handleLock,
    messageRef,
    mobileContainer,
    onScroll,
    onTouchMove,
    onWheel,
  };
};
